import { useEffect, useState } from "react";
import { OfficerLayout } from "@/components/OfficerLayout";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { StatutoryRegister, StatutoryTabs } from "@/components/StatutoryRegister";
import { formatINR } from "@/lib/i18n";
import { FileBarChart, Download, Calendar } from "lucide-react";

const TABS = [
  { k: "demand-register", labelEn: "Demand Register", labelHi: "मांग रजिस्टर" },
  { k: "collection-register", labelEn: "Collection Register", labelHi: "वसूली रजिस्टर" },
  { k: "outstanding-register", labelEn: "Outstanding Register", labelHi: "बकाया रजिस्टर" },
  { k: "statutory", labelEn: "Statutory Registers", labelHi: "वैधानिक रजिस्टर" },
];

export default function Reports() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [tab, setTab] = useState("demand-register");
  const [data, setData] = useState(null);
  const [params, setParams] = useState({ financial_year: "2025-26", start_date: "", end_date: "" });

  const load = () => {
    if (tab === "statutory") return;
    const q = {};
    if (user?.panchayat_id) q.panchayat_id = user.panchayat_id;
    if (tab === "demand-register" && params.financial_year) q.financial_year = params.financial_year;
    if (tab === "collection-register") {
      if (params.start_date) q.start_date = params.start_date;
      if (params.end_date) q.end_date = params.end_date;
    }
    api.get(`/reports/${tab}`, { params: q }).then((r) => setData(r.data));
  };

  useEffect(() => { load(); }, [tab, user]);

  const exportCSV = () => {
    if (!data?.rows) return;
    const rows = data.rows;
    const keys = Object.keys(rows[0] || {});
    const csv = [
      keys.join(","),
      ...rows.map((r) => keys.map((k) => JSON.stringify(r[k] ?? "")).join(",")),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${tab}-${Date.now()}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  return (
    <OfficerLayout>
      <div>
        <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#E87722]">{lang === "hi" ? "रिपोर्ट" : "Reports"}</div>
            <h1 className="mt-2 font-heading text-3xl font-bold text-slate-900 flex items-center gap-2">
              <FileBarChart className="w-7 h-7 text-[#0A3161]" />
              {lang === "hi" ? "रजिस्टर एवं रिपोर्ट" : "Registers & Reports"}
            </h1>
          </div>
          {tab !== "statutory" && (
          <button onClick={exportCSV} data-testid="report-export" className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-md border border-[#0A3161] text-[#0A3161]">
            <Download className="w-4 h-4" /> Export CSV
          </button>
          )}
        </div>

        <div className="flex border-b border-slate-200 mb-4">
          {TABS.map((x) => (
            <button key={x.k} onClick={() => setTab(x.k)} data-testid={`rep-tab-${x.k}`}
              className={`px-5 py-3 text-sm font-semibold border-b-2 transition-colors ${
                tab === x.k ? "border-[#E87722] text-[#0A3161]" : "border-transparent text-slate-500"
              }`}>
              {lang === "hi" ? x.labelHi : x.labelEn}
            </button>
          ))}
        </div>

        {/* Filters */}
        {tab !== "statutory" && (
        <div className="mb-4 p-3 rounded-md border border-slate-200 bg-white flex items-center gap-3 flex-wrap">
          {tab === "demand-register" && (
            <>
              <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{t("financialYear")}</span>
              <input value={params.financial_year} onChange={(e) => setParams({ ...params, financial_year: e.target.value })}
                placeholder="2025-26"
                className="px-3 py-1.5 rounded-md border border-slate-300 bg-white text-sm w-32" data-testid="rep-fy" />
            </>
          )}
          {tab === "collection-register" && (
            <>
              <Calendar className="w-4 h-4 text-slate-500" />
              <input type="date" value={params.start_date} onChange={(e) => setParams({ ...params, start_date: e.target.value })}
                className="px-3 py-1.5 rounded-md border border-slate-300 bg-white text-sm" data-testid="rep-start" />
              <span className="text-slate-500 text-xs">→</span>
              <input type="date" value={params.end_date} onChange={(e) => setParams({ ...params, end_date: e.target.value })}
                className="px-3 py-1.5 rounded-md border border-slate-300 bg-white text-sm" data-testid="rep-end" />
            </>
          )}
          <button onClick={load} className="px-3 py-1.5 text-xs font-semibold rounded-md text-white" style={{ background: "#0A3161" }} data-testid="rep-apply">Apply</button>
        </div>
        )}

        {/* Summary tiles */}
        {data && tab !== "statutory" && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            {tab === "demand-register" && (
              <>
                <Tile label={t("totalDemand")} value={formatINR(data.total_demand)} />
                <Tile label={t("totalCollected")} value={formatINR(data.total_paid)} />
                <Tile label="Records" value={data.rows?.length || 0} />
              </>
            )}
            {tab === "collection-register" && (
              <>
                <Tile label="Receipts" value={data.count} />
                <Tile label={t("totalCollected")} value={formatINR(data.total_collected)} />
              </>
            )}
            {tab === "outstanding-register" && (
              <>
                <Tile label="Defaulters" value={data.count} />
                <Tile label={t("outstanding")} value={formatINR(data.total_outstanding)} />
              </>
            )}
          </div>
        )}

        {/* Tables */}
        {tab === "statutory" ? (
          <RegistersSection />
        ) : (
        <div className="rounded-md border border-slate-200 bg-white overflow-x-auto">
          {tab === "demand-register" && <DemandTable rows={data?.rows || []} t={t} />}
          {tab === "collection-register" && <CollectionTable rows={data?.rows || []} t={t} />}
          {tab === "outstanding-register" && <OutstandingTable rows={data?.rows || []} t={t} />}
        </div>
        )}
      </div>
    </OfficerLayout>
  );
}

const Tile = ({ label, value }) => (
  <div className="rounded-md border border-slate-200 bg-white p-4">
    <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">{label}</div>
    <div className="mt-1 font-heading text-xl font-bold text-slate-900">{value}</div>
  </div>
);

function RegistersSection() {
  const [f, setF] = useState("VII");
  return (
    <div data-testid="statutory-registers-section">
      <StatutoryTabs codes={["VII", "XV", "XVI", "XIX", "XX"]} active={f} onChange={setF} />
      <StatutoryRegister code={f} />
    </div>
  );
}

function DemandTable({ rows, t }) {
  return (
    <table className="w-full text-sm" data-testid="report-table-demand">
      <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
        <tr>
          <th className="text-left px-3 py-3">FY</th>
          <th className="text-left px-3 py-3">{t("propertyNo")}</th>
          <th className="text-left px-3 py-3">{t("owner")}</th>
          <th className="text-left px-3 py-3">{t("ward")}</th>
          <th className="text-right px-3 py-3">{t("totalAmount")}</th>
          <th className="text-right px-3 py-3">{t("paidAmount")}</th>
          <th className="text-left px-3 py-3">{t("status")}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((d) => (
          <tr key={d.id} className="border-t border-slate-100">
            <td className="px-3 py-2 font-mono text-xs">{d.financial_year}</td>
            <td className="px-3 py-2 font-mono text-xs">{d.property_no}</td>
            <td className="px-3 py-2">{d.owner_name}</td>
            <td className="px-3 py-2">{d.ward}</td>
            <td className="px-3 py-2 text-right font-mono">₹{d.total_amount?.toLocaleString("en-IN")}</td>
            <td className="px-3 py-2 text-right font-mono">₹{d.paid_amount?.toLocaleString("en-IN")}</td>
            <td className="px-3 py-2 capitalize">{d.status}</td>
          </tr>
        ))}
        {rows.length === 0 && <tr><td colSpan={7} className="py-10 text-center text-slate-400">No records</td></tr>}
      </tbody>
    </table>
  );
}

function CollectionTable({ rows, t }) {
  return (
    <table className="w-full text-sm" data-testid="report-table-collection">
      <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
        <tr>
          <th className="text-left px-3 py-3">{t("receiptNo")}</th>
          <th className="text-left px-3 py-3">{t("paidAt")}</th>
          <th className="text-left px-3 py-3">{t("propertyNo")}</th>
          <th className="text-left px-3 py-3">{t("owner")}</th>
          <th className="text-left px-3 py-3">{t("paymentMode")}</th>
          <th className="text-right px-3 py-3">{t("amount") || "Amount"}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id} className="border-t border-slate-100">
            <td className="px-3 py-2 font-mono text-xs">{r.receipt_no}</td>
            <td className="px-3 py-2 text-xs">{new Date(r.paid_at).toLocaleString("en-IN")}</td>
            <td className="px-3 py-2 font-mono text-xs">{r.property_no}</td>
            <td className="px-3 py-2">{r.owner_name}</td>
            <td className="px-3 py-2 uppercase text-xs">{r.payment_mode}</td>
            <td className="px-3 py-2 text-right font-mono">₹{r.amount_paid?.toLocaleString("en-IN")}</td>
          </tr>
        ))}
        {rows.length === 0 && <tr><td colSpan={6} className="py-10 text-center text-slate-400">No collections</td></tr>}
      </tbody>
    </table>
  );
}

function OutstandingTable({ rows, t }) {
  return (
    <table className="w-full text-sm" data-testid="report-table-outstanding">
      <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
        <tr>
          <th className="text-left px-3 py-3">{t("propertyNo")}</th>
          <th className="text-left px-3 py-3">{t("owner")}</th>
          <th className="text-left px-3 py-3">{t("mobile")}</th>
          <th className="text-left px-3 py-3">FY</th>
          <th className="text-right px-3 py-3">{t("totalAmount")}</th>
          <th className="text-right px-3 py-3">{t("paidAmount")}</th>
          <th className="text-right px-3 py-3">{t("outstanding")}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((d) => (
          <tr key={d.id} className="border-t border-slate-100">
            <td className="px-3 py-2 font-mono text-xs">{d.property_no}</td>
            <td className="px-3 py-2">{d.owner_name}</td>
            <td className="px-3 py-2 font-mono text-xs">{d.mobile}</td>
            <td className="px-3 py-2 font-mono text-xs">{d.financial_year}</td>
            <td className="px-3 py-2 text-right font-mono">₹{d.total_amount?.toLocaleString("en-IN")}</td>
            <td className="px-3 py-2 text-right font-mono">₹{d.paid_amount?.toLocaleString("en-IN")}</td>
            <td className="px-3 py-2 text-right font-mono font-bold text-[#E87722]">₹{d.outstanding?.toLocaleString("en-IN")}</td>
          </tr>
        ))}
        {rows.length === 0 && <tr><td colSpan={7} className="py-10 text-center text-slate-400">No outstanding records</td></tr>}
      </tbody>
    </table>
  );
}
